'use client';

import React, { useCallback, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useRouter } from 'next/navigation';

import {
  BETBY_BETSLIP_Z_INDEX,
  BETBY_BOTTOM_NAV_SELECTOR,
  BETBY_DEFAULT_LANG,
  BETBY_HEADER_SELECTOR,
} from '@/constants/betby';
import { routes } from '@/features/v2/constants/routes';
import type { BetbyInitializeOptions, BetbyRendererInstance } from '@/types/betby';
import { useMountEffect } from '@/hooks/useMountEffect';
import { useAuthStore } from '@/stores/userStore';
import { userUIStore } from '@/stores/userUIStore';
import { setBetbyRenderer } from '@/utils/betbyNavigation';
import { measureBetbyLayoutOffsets } from '@/utils/betbyOffsets';

import { useAuth } from '@/core/auth/client/AuthProvider';

const LoginModal = dynamic(
  () => {
    return import('@/components/modals/LoginModal');
  },
  {
    ssr: false,
    loading: () => {
      return null;
    },
  },
);

interface BetbySportsBookShellProps {
  userId?: string;
  scriptUrl: string;
  brandId: string;
  themeName: string;
  onLoginRequest: () => void;
  onRegisterRequest: () => void;
  onRechargeRequest: () => void;
}

const fetchBetbyToken = async (): Promise<string | null> => {
  const response = await fetch('/api/integrations/betby/token', {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json() as { token?: string };
  return data.token ?? null;
};

const BetbySportsBookShell = ({
  userId,
  scriptUrl,
  brandId,
  themeName,
  onLoginRequest,
  onRegisterRequest,
  onRechargeRequest,
}: BetbySportsBookShellProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<BetbyRendererInstance | null>(null);
  const hasInitStartedRef = useRef(false);
  const initGenerationRef = useRef(0);

  const onLoginRef = useRef(onLoginRequest);
  const onRegisterRef = useRef(onRegisterRequest);
  const onRechargeRef = useRef(onRechargeRequest);

  onLoginRef.current = onLoginRequest;
  onRegisterRef.current = onRegisterRequest;
  onRechargeRef.current = onRechargeRequest;

  const syncOffsets = useCallback(() => {
    if (!rendererRef.current) {
      return;
    }

    const offsets = measureBetbyLayoutOffsets();
    rendererRef.current.updateOptions({
      stickyTop: offsets.stickyTop,
      betSlipOffsetTop: offsets.betSlipOffsetTop,
      betSlipOffsetBottom: offsets.betSlipOffsetBottom,
    });
  }, []);

  const initializeRenderer = useCallback(async () => {
    if (hasInitStartedRef.current) {
      return;
    }

    if (!window.BTRenderer || !containerRef.current) {
      return;
    }

    hasInitStartedRef.current = true;
    const generation = initGenerationRef.current + 1;
    initGenerationRef.current = generation;

    try {
      const token = userId ? await fetchBetbyToken() : null;

      if (generation !== initGenerationRef.current) {
        return;
      }

      const offsets = measureBetbyLayoutOffsets();

      rendererRef.current?.kill();

      const initializeOptions: BetbyInitializeOptions = {
        brand_id: brandId,
        token,
        themeName,
        lang: BETBY_DEFAULT_LANG,
        target: containerRef.current,
        betSlipOffsetTop: offsets.betSlipOffsetTop,
        betSlipOffsetBottom: offsets.betSlipOffsetBottom,
        stickyTop: offsets.stickyTop,
        betslipZIndex: BETBY_BETSLIP_Z_INDEX,
        basename: '/deportes',
        onLogin: () => {
          onLoginRef.current();
        },
        onRegister: () => {
          onRegisterRef.current();
        },
        onRecharge: () => {
          onRechargeRef.current();
        },
        onSessionRefresh: () => {
          window.location.reload();
        },
      };

      if (userId) {
        initializeOptions.onTokenExpired = async () => {
          const refreshedToken = await fetchBetbyToken();
          if (!refreshedToken) {
            throw new Error('Unable to refresh Betby token');
          }
          return refreshedToken;
        };
      }

      rendererRef.current = new window.BTRenderer().initialize(initializeOptions);
      setBetbyRenderer(rendererRef.current);
      syncOffsets();
    } catch {
      hasInitStartedRef.current = false;
      setBetbyRenderer(null);
    }
  }, [brandId, syncOffsets, themeName, userId]);

  useMountEffect(() => {
    window.handleBetbySignInClick = () => {
      onLoginRef.current();
    };

    if (window.BTRenderer) {
      void initializeRenderer();
    }

    const resizeObserver = new ResizeObserver(() => {
      syncOffsets();
    });

    const header = document.querySelector(BETBY_HEADER_SELECTOR);
    const bottomNav = document.querySelector(BETBY_BOTTOM_NAV_SELECTOR);

    if (header) {
      resizeObserver.observe(header);
    }

    if (bottomNav) {
      resizeObserver.observe(bottomNav);
    }

    window.addEventListener('resize', syncOffsets);

    return () => {
      initGenerationRef.current += 1;
      hasInitStartedRef.current = false;
      delete window.handleBetbySignInClick;
      window.removeEventListener('resize', syncOffsets);
      resizeObserver.disconnect();
      setBetbyRenderer(null);
      rendererRef.current?.kill();
      rendererRef.current = null;
    };
  });

  return (
    <>
      <Script
        src={scriptUrl}
        strategy="afterInteractive"
        onReady={() => {
          void initializeRenderer();
        }}
      />
      <div
        ref={containerRef}
        id="betby"
        className="min-h-[calc(100vh-200px)] w-full"
      />
    </>
  );
};

const BetbyPageFrame = ({ children }: { children: React.ReactNode }) => {
  const { isSidebarOpen } = userUIStore();

  return (
    <div className={`${isSidebarOpen ? 'pl-72' : 'pl-20'} w-full pt-20 max-lg:pt-[3rem] max-md:pl-1 bg-default-50/30`}>
      <div className="text-default-900 flex flex-col items-stretch w-full">
        {children}
      </div>
    </div>
  );
};

export const BetbySportsBook = () => {
  const { session, loading: isAuthLoading } = useAuth();
  const { account } = useAuthStore();
  const { toggleLoginModal } = userUIStore();
  const router = useRouter();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const scriptUrl = process.env.NEXT_PUBLIC_BETBY_SCRIPT_URL ?? '';
  const brandId = process.env.NEXT_PUBLIC_BETBY_BRAND_ID ?? '';
  const themeName = process.env.NEXT_PUBLIC_BETBY_THEME_NAME ?? 'default';

  useMountEffect(() => {
    if (account?.contingencies?.bets) {
      router.push('/?cont=2');
    }
  });

  const handleLoginRequest = () => {
    setShowLoginModal(true);
    toggleLoginModal();
  };

  const handleRegisterRequest = () => {
    router.push(routes.REGISTER);
  };

  const handleRechargeRequest = () => {
    router.push('/deposito');
  };

  const userId = session?.user?.id;

  if (account?.contingencies?.bets) {
    return (
      <BetbyPageFrame>
        <div className="flex flex-col items-center justify-center h-screen">
          <div className="flex flex-col items-center justify-center bg-danger p-6 rounded-xl">
            <h1 className="text-2xl font-bold">
              Contingencia de pronosticos deportivas
            </h1>
            <p className="text-lg">
              Comunicate con el servicio de pronosticos para resolver el
              problema.
            </p>
          </div>
        </div>
      </BetbyPageFrame>
    );
  }

  // Wait for auth to settle so we don't mount as guest then remount as user.
  if (isAuthLoading) {
    return (
      <BetbyPageFrame>
        <div className="min-h-[calc(100vh-200px)] w-full" />
      </BetbyPageFrame>
    );
  }

  return (
    <>
      <BetbyPageFrame>
        <BetbySportsBookShell
          key={userId ?? 'guest'}
          userId={userId}
          scriptUrl={scriptUrl}
          brandId={brandId}
          themeName={themeName}
          onLoginRequest={handleLoginRequest}
          onRegisterRequest={handleRegisterRequest}
          onRechargeRequest={handleRechargeRequest}
        />
      </BetbyPageFrame>
      {showLoginModal && <LoginModal />}
    </>
  );
};
